/**
 * Сетка вахт «люди × дни» (РС-06): план и факт в одной сетке на месяц, заполнение вахт по графику
 * (15/15, 28/28, 30/30), проверка месяца. В клетке буква, а не только цвет: «п» — план, «ф» — факт, «пф» — оба.
 */
import type { RotationFillResult, RotationGridView, WorkerRow } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api } from '../../lib/api';
import { Button, IconButton } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { LField } from '../schedule/LField';

const CELL: Record<string, { letter: string; cls: string; title: string }> = {
  none: { letter: '', cls: '', title: 'Не на вахте' },
  plan: {
    letter: 'п',
    cls: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200',
    title: 'По плану на вахте',
  },
  fact: {
    letter: 'ф',
    cls: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
    title: 'Факт вне плана',
  },
  both: {
    letter: 'пф',
    cls: 'bg-emerald-200 text-emerald-950 dark:bg-emerald-900 dark:text-emerald-100',
    title: 'План и факт',
  },
};
const ISSUE_TITLES: Record<string, string> = {
  overlap: 'Пересечение вахт',
  no_arrival: 'Заезд не отмечен',
  late_arrival: 'Поздний заезд',
  late_departure: 'Поздний выезд',
  early_departure: 'Ранний выезд',
  fact_without_plan: 'Факт вне плана',
  short_rest: 'Короткий отдых',
};
const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];
const d2 = (s: string | null | undefined) => (s ? s.slice(0, 10).split('-').reverse().join('.') : '—');
const shiftMonth = (m: string, by: number) => {
  const d = new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1 + by, 1));
  return d.toISOString().slice(0, 7);
};

export function RotationGridPanel({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const key = ['rotation-grid', projectId, month];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<RotationGridView>(`/projects/${projectId}/rotation-grid`, { query: { month } }),
  });
  const ws = useQuery({
    queryKey: ['workers', projectId],
    queryFn: () => api<WorkerRow[]>('/workers', { query: { projectId } }),
    enabled: canEdit,
  });
  const [f, setF] = useState({ pattern: '15/15', firstStart: '', until: '' });
  const [picked, setPicked] = useState<string[]>([]);
  const fill = useMutation({
    mutationFn: () =>
      api<RotationFillResult>(`/projects/${projectId}/rotations/fill`, {
        method: 'POST',
        body: { workerIds: picked, ...f },
      }),
    onSuccess: (r) => {
      toast(
        r.skipped
          ? `Создано вахт: ${r.created}; пропущено ${r.skipped} — на эти даты у человека уже есть вахта`
          : `Создано вахт: ${r.created}`,
      );
      setPicked([]);
      qc.invalidateQueries({ queryKey: ['rotation-grid', projectId] });
      qc.invalidateQueries({ queryKey: ['rotations', projectId] });
      qc.invalidateQueries({ queryKey: ['resource-load'] });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!picked.length || !f.firstStart || !f.until) {
      toast('Выберите людей и укажите первый заезд и конечную дату', 'danger');
      return;
    }
    fill.mutate();
  };
  const title = `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
  const v = q.data;
  return (
    <Panel
      title="Сетка вахт: люди × дни"
      actions={
        <span className="flex items-center gap-1">
          <IconButton
            label="Предыдущий месяц"
            onClick={() => setMonth(shiftMonth(month, -1))}
            className="size-11 md:size-9"
          >
            <ChevronLeft className="size-4" />
          </IconButton>
          <span className="min-w-32 text-center text-[14px] font-medium capitalize">{title}</span>
          <IconButton
            label="Следующий месяц"
            onClick={() => setMonth(shiftMonth(month, 1))}
            className="size-11 md:size-9"
          >
            <ChevronRight className="size-4" />
          </IconButton>
        </span>
      }
    >
      <div className="flex flex-col gap-4">
        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : !v?.workers.length ? (
          <EmptyState title="В этом месяце вахт нет">
            Заполните вахты по графику ниже или добавьте заезд вручную.
          </EmptyState>
        ) : (
          <>
            <p className="text-[12px] text-ink-2">
              «п» — по плану на вахте, «ф» — на вахте по факту вне плана, «пф» — план и факт совпали; серым —
              выходные.
            </p>
            <ScrollX>
              <table className="w-full border-collapse text-[11px]">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 min-w-36 bg-surface px-2 py-1 text-left font-medium">
                      Человек
                    </th>
                    {v.days.map((d, i) => (
                      <th
                        key={d}
                        className={clsx(
                          'min-w-7 px-0.5 py-1 text-center font-normal',
                          v.weekends.includes(i) && 'bg-line/50',
                          d === v.today && 'font-semibold text-accent',
                        )}
                      >
                        {Number(d.slice(8))}
                      </th>
                    ))}
                    <th className="px-2 py-1 text-right font-medium">План / факт, дн.</th>
                  </tr>
                </thead>
                <tbody>
                  {v.workers.map((w) => (
                    <tr key={w.workerId} className="border-t border-line">
                      <td className="sticky left-0 z-10 bg-surface px-2 py-1 text-[13px]">
                        {w.workerName}
                        <span className="block text-[11px] text-ink-2">{w.patterns.join(', ')}</span>
                      </td>
                      {w.cells.map((c, i) => (
                        <td
                          key={v.days[i]}
                          title={`${w.workerName}, ${d2(v.days[i])}: ${CELL[c]!.title}`}
                          className={clsx(
                            'h-8 text-center',
                            CELL[c]!.cls,
                            c === 'none' && v.weekends.includes(i) && 'bg-line/50',
                          )}
                        >
                          {CELL[c]!.letter}
                        </td>
                      ))}
                      <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">
                        {w.planDays} / {w.factDays}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-line font-medium">
                    <td className="sticky left-0 z-10 bg-surface px-2 py-1">На вахте по плану</td>
                    {v.planTotals.map((n, i) => (
                      <td key={v.days[i]} className="text-center tabular-nums">
                        {n || ''}
                      </td>
                    ))}
                    <td />
                  </tr>
                  <tr className="font-medium">
                    <td className="sticky left-0 z-10 bg-surface px-2 py-1">На вахте по факту</td>
                    {v.factTotals.map((n, i) => (
                      <td key={v.days[i]} className="text-center tabular-nums">
                        {n || ''}
                      </td>
                    ))}
                    <td />
                  </tr>
                </tbody>
              </table>
            </ScrollX>
            <div>
              <h3 className="mb-1 text-[13px] font-medium">Проверка месяца</h3>
              {v.issues.length ? (
                <ul className="divide-y divide-line">
                  {v.issues.map((x) => (
                    <li
                      key={`${x.workerId}-${x.rotationId}-${x.kind}-${x.day ?? ''}-${x.text}`}
                      className="flex flex-wrap items-center gap-2 py-1.5 text-[13px]"
                    >
                      <Badge tone={x.kind === 'overlap' || x.kind === 'no_arrival' ? 'danger' : 'warning'}>
                        {ISSUE_TITLES[x.kind]}
                      </Badge>
                      <span className="font-medium">{x.workerName}</span>
                      <span className="text-ink-2">
                        {d2(x.day)} · {x.text}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[13px] text-ink-2">
                  Замечаний нет: пересечений, пропущенных заездов и расхождений с планом не найдено.
                </p>
              )}
            </div>
          </>
        )}
        {canEdit ? (
          <form onSubmit={submit} className="flex flex-col gap-3 border-t border-line pt-4">
            <h3 className="text-[13px] font-medium">Заполнить вахты по графику</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <LField label="График">
                <Select
                  value={f.pattern}
                  onChange={(e) => setF({ ...f, pattern: e.target.value })}
                  className="max-md:h-11"
                >
                  <option value="15/15">15/15</option>
                  <option value="28/28">28/28</option>
                  <option value="30/30">30/30</option>
                </Select>
              </LField>
              <LField label="Первый заезд">
                <Input
                  type="date"
                  value={f.firstStart}
                  onChange={(e) => setF({ ...f, firstStart: e.target.value })}
                  className="max-md:h-11"
                />
              </LField>
              <LField label="Заполнить до" hint="Последний заезд — не позже этой даты">
                <Input
                  type="date"
                  value={f.until}
                  onChange={(e) => setF({ ...f, until: e.target.value })}
                  className="max-md:h-11"
                />
              </LField>
            </div>
            <fieldset className="flex flex-wrap gap-2">
              <legend className="mb-1 text-[12px] text-ink-2">Кто едет на вахты</legend>
              {(ws.data ?? [])
                .filter((w) => w.status === 'active')
                .map((w) => (
                  <label
                    key={w.id}
                    className="flex min-h-11 items-center gap-2 rounded-md border border-line px-3 text-[13px]"
                  >
                    <input
                      type="checkbox"
                      checked={picked.includes(w.id)}
                      onChange={(e) =>
                        setPicked(e.target.checked ? [...picked, w.id] : picked.filter((x) => x !== w.id))
                      }
                    />
                    {w.fullName}
                  </label>
                ))}
            </fieldset>
            <div>
              <Button type="submit" variant="primary" disabled={fill.isPending} className="max-md:h-11">
                Создать вахты
              </Button>
              <p className="mt-1 text-[12px] text-ink-2">
                Циклы «работа/отдых» идут подряд от первого заезда; если у человека уже есть вахта на эти
                даты, цикл пропускается.
              </p>
            </div>
          </form>
        ) : null}
      </div>
    </Panel>
  );
}
